/**
 * B2B Theme Injector - Main Entry Point
 *
 * Propagates B2B theme CSS variables to web components (Shadow DOM).
 * The @theme directive defines variables on both :root and :host with default orange values.
 * We need to inject an override into each web component's Shadow DOM to use purple for B2B.
 *
 * Architecture:
 * - Uses MutationObserver as primary mechanism (catches dynamic components)
 * - Fallback polling only for initial load (stops after components are found)
 * - Single processing function to avoid redundancy
 * - Debounced operations to prevent performance issues
 */

import {
  clearSparkHash,
  handleSubdomainRedirect,
  openShopifyFormsPopup,
} from './b2b-theme-injector/b2b-utils.js';
import {
  interceptCustomElementsDefine,
  setupInitialPolling,
  setupMutationObserver,
} from './b2b-theme-injector/component-discovery.js';
import { rewriteB2BLinksOnce } from './b2b-theme-injector/link-rewriter.js';
import { isB2B, isWholesaleSubdomain } from './b2b-theme-injector/config.js';

// Early exit if not B2B context
if (!isB2B && !isWholesaleSubdomain) {
  // Exit early - modules won't be needed
  // Note: In ES modules, we can't use return, so we wrap everything in conditional
} else {
  /**
   * Handle subdomain redirect (must run immediately, before any other logic)
   * Only redirect when on wholesale subdomain WITHOUT B2B layout already applied.
   * If isB2B is true, we're already using b2b-theme.liquid, so no redirect needed.
   */
  if (!isB2B && isWholesaleSubdomain) {
    handleSubdomainRedirect();
  }

  /**
   * Clear SparkLayer hash after load
   */
  window.addEventListener('load', () => {
    window.setTimeout(clearSparkHash, 800);
  });

  /**
   * Contact sales button handler
   */
  document.addEventListener('click', (e) => {
    const btn =
      e.target && e.target.closest ? e.target.closest('[data-b2b-contact-sales-trigger]') : null;
    if (!btn) return;

    e.preventDefault();

    // Store context for later workflows/debugging (optional).
    try {
      const handle = btn.getAttribute('data-product-handle') || '';
      const sku = btn.getAttribute('data-variant-sku') || '';
      sessionStorage.setItem('b2bContactSalesProductHandle', handle);
      sessionStorage.setItem('b2bContactSalesVariantSku', sku);
    } catch {
      // ignore
    }

    const opened = openShopifyFormsPopup();
    if (!opened) {
      console.warn(
        '[B2B] Contact sales clicked, but no Shopify Forms popup embed/teaser was found. Ensure the Forms app embed is enabled and the popup teaser is enabled.'
      );
    }
  });

  /**
   * Rewrite product and collection links to include ?view=b2b
   * This ensures B2B template selection when users click links
   */
  rewriteB2BLinksOnce();

  /**
   * Initialize component discovery and processing
   */
  setupInitialPolling();
  setupMutationObserver();
  interceptCustomElementsDefine();
}
